import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { TabsContent } from '@/components/ui/tabs';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Shield, Award, BookOpen, ChevronUp, ChevronDown, ChevronDownIcon, X } from 'lucide-react';

// US States for dropdown
const US_STATES = [
  { value: 'AL', label: 'Alabama' }, { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' }, { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' }, { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' }, { value: 'DE', label: 'Delaware' },
  { value: 'FL', label: 'Florida' }, { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' }, { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' }, { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' }, { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' }, { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' }, { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' }, { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' }, { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' }, { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' }, { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' }, { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' }, { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' }, { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' }, { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' }, { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' }, { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' }, { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' }, { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' }, { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' }, { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' }, { value: 'WY', label: 'Wyoming' },
  { value: 'DC', label: 'District of Columbia' }
];

const ENTITY_TYPES = [
  { value: 'LLC', label: 'LLC' },
  { value: 'Corporation', label: 'Corporation' },
  { value: 'S-Corp', label: 'S-Corporation' },
  { value: 'Partnership', label: 'Partnership' },
  { value: 'LP', label: 'Limited Partnership' },
  { value: 'LLP', label: 'Limited Liability Partnership' },
  { value: 'Sole Proprietorship', label: 'Sole Proprietorship' }
];

const OFFICER_ROLES = [
  { value: 'President', label: 'President' },
  { value: 'Vice President', label: 'Vice President' },
  { value: 'Treasurer', label: 'Treasurer' },
  { value: 'Secretary', label: 'Secretary' },
  { value: 'Manager', label: 'Manager' },
  { value: 'Managing Member', label: 'Managing Member' },
  { value: 'Member', label: 'Member' },
  { value: 'Other', label: 'Other' }
];

// Multi-select role component
const RoleMultiSelect = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  
  // Parse value - could be string (single), comma-separated, or array
  const selectedRoles = value 
    ? (typeof value === 'string' ? value.split(',').map(r => r.trim()).filter(Boolean) : value)
    : [];
  
  const toggleRole = (role) => {
    let newRoles;
    if (selectedRoles.includes(role)) {
      newRoles = selectedRoles.filter(r => r !== role);
    } else {
      newRoles = [...selectedRoles, role];
    }
    // Store as comma-separated string for backend compatibility
    onChange(newRoles.join(', '));
  };
  
  const removeRole = (role, e) => {
    e.stopPropagation();
    const newRoles = selectedRoles.filter(r => r !== role);
    onChange(newRoles.join(', '));
  };
  
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between border border-slate-200 bg-white hover:border-slate-300 font-normal h-auto min-h-[40px] py-1.5"
        >
          <div className="flex flex-wrap gap-1 flex-1">
            {selectedRoles.length > 0 ? (
              selectedRoles.map(role => (
                <Badge 
                  key={role} 
                  variant="secondary" 
                  className="bg-slate-100 text-slate-700 hover:bg-slate-200 px-2 py-0.5 text-xs"
                >
                  {role}
                  <X 
                    className="ml-1 h-3 w-3 cursor-pointer hover:text-red-500" 
                    onClick={(e) => removeRole(role, e)}
                  />
                </Badge>
              ))
            ) : (
              <span className="text-slate-400">Select roles...</span>
            )}
          </div>
          <ChevronDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[250px] p-2" align="start">
        <div className="space-y-1">
          {OFFICER_ROLES.map(role => (
            <div
              key={role.value}
              className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer hover:bg-slate-100 ${
                selectedRoles.includes(role.value) ? 'bg-amber-50' : ''
              }`}
              onClick={() => toggleRole(role.value)}
            >
              <Checkbox 
                checked={selectedRoles.includes(role.value)}
                className="pointer-events-none"
              />
              <span className="text-sm">{role.label}</span>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export const GovernanceTab = ({ llc, canEditLLC, onFieldUpdate }) => {
  const [entityInfoExpanded, setEntityInfoExpanded] = useState(false);
  const [officersExpanded, setOfficersExpanded] = useState(false);
  const [corporateRecordsExpanded, setCorporateRecordsExpanded] = useState(false);

  if (!llc) return null;

  return (
    <TabsContent value="governance" className="mt-4">
      <div className="space-y-4">
        {/* Entity Information */}
        <Collapsible open={entityInfoExpanded} onOpenChange={setEntityInfoExpanded}>
          <Card className="border-slate-200">
            <CollapsibleTrigger asChild>
              <CardHeader className="flex flex-row items-center justify-between pb-3 cursor-pointer hover:bg-slate-50 transition-colors">
                <div className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-slate-600" />
                  <div>
                    <CardTitle className="font-serif text-lg">Entity Information</CardTitle>
                    <CardDescription>Legal formation details</CardDescription>
                  </div>
                </div>
                {entityInfoExpanded ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent>
                {canEditLLC ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="space-y-1.5">
                      <Label className="text-xs text-slate-500">Entity Type</Label>
                      <Select 
                        value={llc?.entity_type || ''} 
                        onValueChange={(v) => onFieldUpdate({ entity_type: v })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent>
                          {ENTITY_TYPES.map(type => (
                            <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-slate-500">Date Formed</Label>
                      <Input 
                        type="date" 
                        value={llc?.date_formed || ''} 
                        onChange={(e) => onFieldUpdate({ date_formed: e.target.value })}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-slate-500">State Formed</Label>
                      <Select 
                        value={llc?.state_formed || ''} 
                        onValueChange={(v) => onFieldUpdate({ state_formed: v })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select state" />
                        </SelectTrigger>
                        <SelectContent>
                          {US_STATES.map(state => (
                            <SelectItem key={state.value} value={state.value}>{state.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-slate-500">EIN Number</Label>
                      <Input 
                        value={llc?.ein_number || ''} 
                        onChange={(e) => onFieldUpdate({ ein_number: e.target.value })}
                        placeholder="XX-XXXXXXX"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-slate-500">State ID Number</Label>
                      <Input 
                        value={llc?.state_id_number || ''} 
                        onChange={(e) => onFieldUpdate({ state_id_number: e.target.value })}
                        placeholder="State registration number"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-slate-500">Registered Agent</Label>
                      <Input 
                        value={llc?.registered_agent || ''} 
                        onChange={(e) => onFieldUpdate({ registered_agent: e.target.value })}
                        placeholder="Name of registered agent"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                    <div>
                      <p className="text-xs text-slate-500">Entity Type</p>
                      <p className="font-medium">{llc?.entity_type || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Date Formed</p>
                      <p className="font-medium">{llc?.date_formed || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">State Formed</p>
                      <p className="font-medium">{llc?.state_formed || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">EIN Number</p>
                      <p className="font-medium font-mono">{llc?.ein_number || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">State ID Number</p>
                      <p className="font-medium font-mono">{llc?.state_id_number || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Registered Agent</p>
                      <p className="font-medium">{llc?.registered_agent || '-'}</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>

        {/* Officers & Directors */}
        <Collapsible open={officersExpanded} onOpenChange={setOfficersExpanded}>
          <Card className="border-slate-200">
            <CollapsibleTrigger asChild>
              <CardHeader className="flex flex-row items-center justify-between pb-3 cursor-pointer hover:bg-slate-50 transition-colors">
                <div className="flex items-center gap-2">
                  <Award className="w-5 h-5 text-slate-600" />
                  <div>
                    <CardTitle className="font-serif text-lg">Officers & Directors</CardTitle>
                    <CardDescription>Key personnel and their roles</CardDescription>
                  </div>
                </div>
                {officersExpanded ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent>
                {canEditLLC ? (
                  <div className="space-y-4">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-100">
                          <TableHead className="w-[40%] font-semibold">Name</TableHead>
                          <TableHead className="w-[40%] font-semibold">Role</TableHead>
                          <TableHead className="w-[20%] font-semibold text-center">Director</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {[1, 2, 3, 4, 5, 6].map((num) => (
                          <TableRow key={num} className="hover:bg-slate-50">
                            <TableCell className="py-2">
                              <Input 
                                value={llc?.[`officer_${num}_name`] || ''} 
                                onChange={(e) => onFieldUpdate({ [`officer_${num}_name`]: e.target.value })}
                                placeholder="Enter name"
                                className="border border-slate-200 bg-white hover:border-slate-300 focus:border-blue-400 transition-colors"
                              />
                            </TableCell>
                            <TableCell className="py-2">
                              <RoleMultiSelect
                                value={llc?.[`officer_${num}_role`] || ''}
                                onChange={(v) => onFieldUpdate({ [`officer_${num}_role`]: v })}
                              />
                            </TableCell>
                            <TableCell className="py-2 text-center">
                              <Checkbox 
                                checked={llc?.[`officer_${num}_director`] || false}
                                onCheckedChange={(checked) => onFieldUpdate({ [`officer_${num}_director`]: checked })}
                                className="border-slate-300"
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {(() => {
                      const officers = [];
                      for (let i = 1; i <= 6; i++) {
                        const name = llc?.[`officer_${i}_name`];
                        const role = llc?.[`officer_${i}_role`];
                        const isDirector = llc?.[`officer_${i}_director`];
                        if (name || role) {
                          officers.push({ name, role, isDirector });
                        }
                      }
                      
                      if (officers.length === 0) {
                        return <p className="text-slate-400 italic">No officers or directors recorded</p>;
                      }
                      
                      return (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {officers.map((officer, idx) => (
                            <div key={idx} className="flex items-center justify-between p-4 bg-gradient-to-r from-slate-100 to-slate-50 rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
                              <div>
                                <p className="font-semibold text-slate-800">{officer.name}</p>
                                <p className="text-sm text-slate-600">{officer.role}</p>
                              </div>
                              {officer.isDirector && (
                                <Badge className="bg-amber-500 text-white border-0 px-3 py-1 font-medium shadow-sm">
                                  Director
                                </Badge>
                              )}
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                )}
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>

        {/* Corporate Records */}
        <Collapsible open={corporateRecordsExpanded} onOpenChange={setCorporateRecordsExpanded}>
          <Card className="border-slate-200">
            <CollapsibleTrigger asChild>
              <CardHeader className="flex flex-row items-center justify-between pb-3 cursor-pointer hover:bg-slate-50 transition-colors">
                <div className="flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-slate-600" />
                  <div>
                    <CardTitle className="font-serif text-lg">Corporate Records</CardTitle>
                    <CardDescription>Record keeping and management information</CardDescription>
                  </div>
                </div>
                {corporateRecordsExpanded ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent>
                {canEditLLC ? (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-1.5">
                      <Label className="text-xs text-slate-500">Manager / Managing Member</Label>
                      <Input 
                        value={llc?.manager_name || ''} 
                        onChange={(e) => onFieldUpdate({ manager_name: e.target.value })}
                        placeholder="Name of manager"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-slate-500">Minute Book Location</Label>
                      <Input 
                        value={llc?.minute_book_location || ''} 
                        onChange={(e) => onFieldUpdate({ minute_book_location: e.target.value })}
                        placeholder="e.g., Office safe, Attorney's office"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-slate-500">Corporate Seal Location</Label>
                      <Input 
                        value={llc?.corporate_seal_location || ''} 
                        onChange={(e) => onFieldUpdate({ corporate_seal_location: e.target.value })}
                        placeholder="e.g., Office safe"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-xs text-slate-500">Manager / Managing Member</p>
                      <p className="font-medium">{llc?.manager_name || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Minute Book Location</p>
                      <p className="font-medium">{llc?.minute_book_location || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Corporate Seal Location</p>
                      <p className="font-medium">{llc?.corporate_seal_location || '-'}</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>
      </div>
    </TabsContent>
  );
};
